import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen, Clock, ChevronRight, Plus, Flame, Zap,
  AlertCircle, ClipboardList, CheckCircle, TrendingUp,
  AlertTriangle, Lock, Building, Users, Send,
  MoreVertical, Archive, Trash2, ArchiveRestore, Target,
  ChevronDown, Play, Route, Award, ArrowRight, X, PenLine,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSpace } from '../context/SpaceContext';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABEL_KEYS = {
  generating: { key: 'dashboard.generating', color: 'text-[#ffd93d]', bg: 'bg-[#ffd93d]/10' },
  ready:      { key: 'dashboard.ready',      color: 'text-[#20c997]', bg: 'bg-[#20c997]/10' },
  error:      { key: 'dashboard.error',      color: 'text-[#ff6b6b]', bg: 'bg-[#ff6b6b]/10' },
};

const SPACE_ICONS = { famille: Users, organisation: Building, entreprise: Building };

// ── Course context menu ───────────────────────────────────────────────────────
function CourseMenu({ course, onArchive, onUnarchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isArchived = !!course.archived_at;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!e.target.closest(`[data-menuid="${course.id}"]`)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, course.id]);

  return (
    <div data-menuid={course.id} className="relative shrink-0" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid={`course-menu-${course.id}`}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-[#94a3b8] hover:text-white transition-colors"
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-[#1a1927] border border-white/10 rounded-xl shadow-xl z-50 min-w-[160px] overflow-hidden">
          <button
            onClick={() => { setOpen(false); navigate(`/course/${course.id}/edit`); }}
            data-testid={`edit-course-${course.id}`}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/8 transition-colors"
          >
            <PenLine size={13} className="text-[#20c997]" /> Éditer
          </button>
          <div className="border-t border-white/5" />
          {!isArchived ? (
            <button
              onClick={() => { setOpen(false); onArchive(course.id); }}
              data-testid={`archive-course-${course.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/8 transition-colors"
            >
              <Archive size={13} className="text-[#ffd93d]" /> Archiver
            </button>
          ) : (
            <button
              onClick={() => { setOpen(false); onUnarchive(course.id); }}
              data-testid={`unarchive-course-${course.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/8 transition-colors"
            >
              <ArchiveRestore size={13} className="text-[#20c997]" /> Restaurer
            </button>
          )}
          <div className="border-t border-white/5" />
          <button
            onClick={() => { setOpen(false); onDelete(course.id, course.title); }}
            data-testid={`delete-course-${course.id}`}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#ff6b6b] hover:bg-[#ff6b6b]/8 transition-colors"
          >
            <Trash2 size={13} /> Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

// ── Compact course row ────────────────────────────────────────────────────────
function CourseRow({ course, isCompleted, courseSpace, activeSpace, onArchive, onUnarchive, onDelete, onNavigate }) {
  const { t } = useTranslation();
  const stData = STATUS_LABEL_KEYS[course.status] || STATUS_LABEL_KEYS.ready;
  const st = { ...stData, label: t(stData.key) };

  return (
    <div
      className={`bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex items-center gap-3 hover:border-[#3a3945] transition-all ${course.status === 'error' ? 'opacity-60' : ''}`}
      data-testid={`course-card-${course.id}`}
    >
      <button
        onClick={() => onNavigate(course)}
        className="flex items-center gap-3 flex-1 text-left min-w-0"
      >
        <div className="w-9 h-9 bg-[#ff6b6b]/10 rounded-xl flex items-center justify-center shrink-0 relative">
          <BookOpen size={16} className="text-[#ff6b6b]" />
          {isCompleted && (
            <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-[#20c997] rounded-full flex items-center justify-center">
              <CheckCircle size={8} className="text-white" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium text-sm truncate">{course.title}</p>
            {courseSpace && (
              <span className="text-[10px] font-medium text-[#a78bfa] bg-[#6f42c1]/15 border border-[#6f42c1]/25 px-1.5 py-0.5 rounded-full shrink-0">
                {courseSpace.name.slice(0, 10)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {isCompleted
              ? <span className="text-xs text-[#20c997] font-medium">Terminé</span>
              : <span className={`text-xs font-medium ${st.color}`}>{st.label}</span>
            }
            {course.estimated_hours && course.status === 'ready' && (
              <span className="text-xs text-[#64748b] flex items-center gap-0.5">
                <Clock size={10} /> {course.estimated_hours}h
              </span>
            )}
          </div>
        </div>
        {course.status !== 'error' && <ChevronRight size={14} className="text-[#64748b] shrink-0" />}
      </button>

      {course.status === 'error' ? (
        <span className="text-xs text-[#ffd93d] border border-[#ffd93d]/30 px-2 py-1 rounded-lg shrink-0">
          Erreur
        </span>
      ) : (
        <CourseMenu course={course} onArchive={onArchive} onUnarchive={onUnarchive} onDelete={onDelete} />
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { activeSpace, spaces } = useSpace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [topic, setTopic] = useState('');
  const [courses, setCourses] = useState([]);
  const [allProgress, setAllProgress] = useState({});
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [bilanStatus, setBilanStatus] = useState(null);
  const [bilanStep0, setBilanStep0] = useState({});
  const [credits, setCredits] = useState(null);
  const [referentials, setReferentials] = useState([]);
  const [selectedReferentials, setSelectedReferentials] = useState([]);
  const [showRefSection, setShowRefSection] = useState(false);
  const [resendingVerif, setResendingVerif] = useState(false);
  const [verifSent, setVerifSent] = useState(false);
  const [showResourceMode, setShowResourceMode] = useState(false);
  const [resources, setResources] = useState([{ id: 1, type: 'url', value: '' }]);
  const [creationMode, setCreationMode] = useState('ai');
  const [organization, setOrganization] = useState('standard');

  const loadData = useCallback(async () => {
    try {
      const [coursesRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/courses/progress/all`, { withCredentials: true }),
      ]);
      setCourses(coursesRes.data);
      setAllProgress(progressRes.data || {});
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadBilan();
    loadCredits();
    loadReferentials();
    if (searchParams.get('email_verified') === '1') {
      setSearchParams({}, { replace: true });
      refreshUser && refreshUser();
    }
  }, []);

  const loadCredits = async () => {
    try {
      const { data } = await axios.get(`${API}/credits/balance`, { withCredentials: true });
      setCredits(data);
    } catch { /* ignore */ }
  };

  const loadReferentials = async () => {
    try {
      const { data } = await axios.get(`${API}/referentials`, { withCredentials: true });
      setReferentials(data.referentials || []);
    } catch { /* ignore */ }
  };

  const loadBilan = async () => {
    try {
      const { data } = await axios.get(`${API}/bilan`, { withCredentials: true });
      setBilanStatus(data);
      setBilanStep0(data.responses?.step_0 || {});
    } catch {
      setBilanStatus({ completed: false, steps_completed: 0 });
    }
  };

  const handleArchive = async (courseId) => {
    try {
      await axios.put(`${API}/courses/${courseId}/archive`, {}, { withCredentials: true });
      toast.success('Cours archivé');
      loadData();
    } catch { toast.error('Erreur lors de l\'archivage'); }
  };

  const handleUnarchive = async (courseId) => {
    try {
      await axios.put(`${API}/courses/${courseId}/unarchive`, {}, { withCredentials: true });
      toast.success('Cours restauré');
      loadData();
    } catch { toast.error('Erreur lors de la restauration'); }
  };

  const handleDelete = async (courseId, courseTitle) => {
    if (!window.confirm(`Supprimer définitivement "${courseTitle}" ?`)) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
      toast.success('Cours supprimé');
      loadData();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleNavigateCourse = (course) => {
    if (course.status === 'generating') navigate(`/generate/${course.id}`);
    else if (course.status === 'ready') navigate(`/course/${course.id}`);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      setError('Entrez un sujet pour générer votre cours.');
      return;
    }
    if (user?.is_verified === false) {
      setError('Vérifiez votre email avant de générer un cours.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const payload = { topic };
      if (activeSpace) payload.space_id = activeSpace.id;
      if (selectedReferentials.length > 0) payload.referential_ids = selectedReferentials;
      if (organization && organization !== 'standard') payload.organization = organization;

      // Vérifier si des ressources sont actives
      const activeResources = showResourceMode
        ? resources
            .filter(r => r.value.trim())
            .map(r => ({
              type: r.type === 'text' ? 'text' : r.type === 'youtube' ? 'video' : 'url',
              url: r.type !== 'text' ? r.value.trim() : undefined,
              text: r.type === 'text' ? r.value.trim() : undefined,
            }))
        : [];

      if (activeResources.length > 0) {
        payload.resources = activeResources;
        const { data } = await axios.post(`${API}/courses/generate-resources`, payload, { withCredentials: true });
        navigate(`/generate/${data.course_id}`);
      } else {
        const { data } = await axios.post(`${API}/courses/generate`, payload, { withCredentials: true });
        navigate(`/generate/${data.course_id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'explorateur_no_ai') {
        setError('Passez à un plan payant pour générer des formations IA.');
      } else if (detail === 'email_not_verified') {
        setError('Vérifiez votre email avant de générer un cours.');
      } else if (err.response?.status === 403) {
        setError(detail || 'Crédits insuffisants.');
        navigate('/credits');
      } else if (err.response?.status === 429) {
        setError(detail || 'Trop de tentatives. Réessayez dans 1 heure.');
      } else {
        setError('Erreur lors de la génération. Réessayez.');
      }
      setGenerating(false);
    }
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.greetingMorning');
    if (h < 18) return t('dashboard.greetingAfternoon');
    return t('dashboard.greetingEvening');
  };

  function getBilanName(step0 = {}) {
    const age = step0.age_range || '';
    const sit = step0.situation || '';
    if (['15–17 ans', '18–25 ans'].includes(age) && ['Étudiant(e)', "En recherche d'emploi"].includes(sit)) return 'Mon Bilan de départ';
    if (['26–35 ans', '36–50 ans', 'Plus de 50 ans'].includes(age) && ['En emploi', 'En reconversion'].includes(sit)) return 'Mon Bilan de compétences';
    return 'Mon Profil Kurso';
  }

  const NAMED_LEVELS = [
    { min: 0,    max: 499,      name: 'Curieux',     next: 'Explorateur', color: '#e8e8e8' },
    { min: 500,  max: 1499,     name: 'Explorateur', next: 'Engagé',      color: '#ff6b6b' },
    { min: 1500, max: 3999,     name: 'Engagé',      next: 'Expert',      color: '#20c997' },
    { min: 4000, max: Infinity, name: 'Expert',      next: null,          color: '#ffd93d' },
  ];
  const getLevel = (xp) => {
    for (const lvl of NAMED_LEVELS) {
      if (xp <= lvl.max) {
        const current = xp - lvl.min;
        const needed = lvl.max === Infinity ? 0 : lvl.max + 1 - lvl.min;
        return { ...lvl, current, needed };
      }
    }
    return { ...NAMED_LEVELS[3], current: 0, needed: 0 };
  };
  const levelInfo = getLevel(user?.xp || 0);

  const suggestions = ['La photosynthèse', 'Le machine learning', 'La Révolution française', 'Les trous noirs', 'La blockchain'];

  // Computed states
  const activeCourses = activeSpace
    ? [...courses.filter(c => c.space_id === activeSpace.id), ...courses.filter(c => c.space_id !== activeSpace.id)]
    : courses;

  const inProgressCourses = activeCourses.filter(c => c.status === 'ready' && !allProgress[c.id]);
  const completedCourses  = activeCourses.filter(c => c.status === 'ready' && allProgress[c.id] === true);
  const lastInProgress    = inProgressCourses[0] || null; // most recent

  const isAiEnabled = credits?.ai_enabled !== false;
  const insufficientCredits = credits && user?.role !== 'admin' && credits.balance < 10;

  const displayedCourses = showAllCourses ? activeCourses : activeCourses.slice(0, 4);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-8 space-y-5">

        {/* Email verification banner */}
        {user?.is_verified === false && (
          <div className="bg-[#ffd93d]/10 border border-[#ffd93d]/30 rounded-2xl p-4" data-testid="email-verification-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-[#ffd93d] mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Vérifiez votre adresse email</p>
                <p className="text-[#94a3b8] text-xs mt-0.5">
                  Un email a été envoyé à <span className="text-white">{user?.email}</span>.
                </p>
                <button
                  onClick={async () => {
                    if (resendingVerif || verifSent) return;
                    setResendingVerif(true);
                    try {
                      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
                      setVerifSent(true);
                    } catch { setError('Impossible de renvoyer le mail.'); }
                    finally { setResendingVerif(false); }
                  }}
                  disabled={resendingVerif || verifSent}
                  className="mt-2 flex items-center gap-1.5 text-xs text-[#ffd93d] border border-[#ffd93d]/30 px-3 py-1.5 rounded-lg hover:bg-[#ffd93d]/10 transition-colors disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  <Send size={11} />
                  {verifSent ? 'Email renvoyé !' : resendingVerif ? 'Envoi…' : 'Renvoyer le mail'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active space badge */}
        {activeSpace && (
          <div className="bg-[#6f42c1]/10 border border-[#6f42c1]/20 rounded-xl p-3" data-testid="active-space-banner">
            <div className="flex items-center gap-2">
              {React.createElement(SPACE_ICONS[activeSpace.type] || Building, { size: 13, className: 'text-[#a78bfa]' })}
              <span className="text-xs text-[#94a3b8]">Espace actif :</span>
              <span className="text-xs font-semibold text-[#a78bfa]">{activeSpace.name}</span>
              <span className="ml-auto text-xs text-[#94a3b8] bg-[#6f42c1]/10 px-2 py-0.5 rounded-full border border-[#6f42c1]/20 capitalize">{activeSpace.type}</span>
            </div>
          </div>
        )}

        {/* ── Greeting + Level ── */}
        <div data-testid="dashboard-greeting">
          <h1 className="font-heading text-2xl font-bold text-white mb-4">
            {getGreeting()}, {user?.name?.split(' ')[0] || 'Apprenant'} !
          </h1>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3" data-testid="stats-row">
            <div className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Zap size={13} className="text-[#ffd93d]" />
                <span className="text-base font-black text-white">{user?.xp || 0}</span>
              </div>
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">XP</p>
            </div>
            <div className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <BookOpen size={13} className="text-[#20c997]" />
                <span className="text-base font-black text-white">{inProgressCourses.length}</span>
              </div>
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">En cours</p>
            </div>
            <div className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                {user?.streak > 0
                  ? <><Flame size={13} className="text-[#ff6b6b]" /><span className="text-base font-black text-[#ff6b6b]">{user.streak}</span></>
                  : <><CheckCircle size={13} className="text-[#94a3b8]" /><span className="text-base font-black text-white">{completedCourses.length}</span></>
                }
              </div>
              <p className="text-[10px] text-[#64748b] uppercase tracking-wide">
                {user?.streak > 0 ? 'Jours consécutifs' : 'Terminés'}
              </p>
            </div>
          </div>

          {/* Level progress bar */}
          <div className="mt-3 bg-[#16161e] border border-[#2a2935] rounded-xl p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold" style={{ color: levelInfo.color }}>{levelInfo.name}</span>
              {levelInfo.next && (
                <span className="text-[10px] text-[#64748b]">{levelInfo.current}/{levelInfo.needed} XP → {levelInfo.next}</span>
              )}
            </div>
            <div className="h-1.5 bg-[#2a2935] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${levelInfo.needed > 0 ? Math.min(100, (levelInfo.current / levelInfo.needed) * 100) : 100}%`,
                  background: `linear-gradient(90deg, ${levelInfo.color}80, ${levelInfo.color})`,
                }}
              />
            </div>
          </div>
        </div>

        {/* ── REPRENDRE — Hero card ── */}
        {lastInProgress && (
          <div
            className="bg-gradient-to-r from-[#16161e] to-[#1a1927] border border-[#20c997]/20 rounded-2xl p-5 flex items-center gap-4"
            data-testid="resume-hero-card"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#20c997]/10 flex items-center justify-center shrink-0">
              <BookOpen size={22} className="text-[#20c997]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#20c997] mb-0.5">Reprendre</p>
              <p className="text-white font-bold text-sm truncate">{lastInProgress.title}</p>
              <p className="text-xs text-[#64748b] mt-0.5 flex items-center gap-1">
                <Clock size={10} /> {lastInProgress.estimated_hours}h estimées
              </p>
            </div>
            <button
              onClick={() => navigate(`/course/${lastInProgress.id}`)}
              data-testid="resume-btn"
              className="shrink-0 flex items-center gap-2 bg-[#20c997] text-[#0f0e17] px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
            >
              <Play size={14} fill="currentColor" /> Reprendre
            </button>
          </div>
        )}

        {/* ── Créer une formation ── */}
        <div data-testid="generation-section">

          {/* Toggle IA / Manuel — caché, mode IA par défaut */}
          <div className="hidden">
            <button onClick={() => setCreationMode('ai')} data-testid="mode-ai-btn" />
            <button onClick={() => setCreationMode('manual')} data-testid="mode-manual-btn" />
          </div>

          {/* ── Mode Manuel (accessible via lien discret) ── */}
          {creationMode === 'manual' && (
            <div className="bg-[#16161e] border border-[#20c997]/20 rounded-2xl p-6 animate-fade-up" data-testid="manual-creation-card">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#20c997]/10 flex items-center justify-center shrink-0">
                  <PenLine size={20} className="text-[#20c997]" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold text-base mb-1">Création manuelle</p>
                  <p className="text-[#94a3b8] text-sm leading-relaxed mb-4">
                    Construisez votre formation module par module, à votre rythme. Sans crédits, disponible sur tous les plans.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Gratuit', 'Structure libre', 'Éditeur visuel inclus'].map(tag => (
                      <span key={tag} className="text-xs bg-[#20c997]/10 text-[#20c997] border border-[#20c997]/20 px-2.5 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate('/course/new')}
                      data-testid="start-manual-creation-btn"
                      className="flex items-center gap-2 bg-[#20c997] text-[#0f0e17] px-5 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
                    >
                      Commencer <ChevronRight size={16} />
                    </button>
                    <button onClick={() => setCreationMode('ai')} className="text-[#64748b] text-xs hover:text-white transition-colors">
                      ← Retour à l'IA
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Mode IA (par défaut, toujours visible) ── */}
          {creationMode === 'ai' && (!isAiEnabled ? (
            <div className="bg-[#16161e] border border-[#ff6b6b]/20 rounded-2xl p-5" data-testid="upgrade-cta">
              <div className="flex items-start gap-3">
                <Lock size={17} className="text-[#ff6b6b] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm mb-1">Passez à un plan payant</p>
                  <p className="text-[#94a3b8] text-xs mb-3">
                    Le plan Explorateur ne donne pas accès à la génération IA. Dès 6,90€/mois pour 200 crédits.
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-2 bg-[#ff6b6b] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
                    data-testid="upgrade-plan-btn"
                  >
                    <Zap size={13} /> Voir les plans <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </div>
          ) : insufficientCredits ? (
            <div className="bg-[#16161e] border border-[#ffd93d]/20 rounded-2xl p-5" data-testid="low-credits-cta">
              <div className="flex items-start gap-3">
                <Zap size={17} className="text-[#ffd93d] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm mb-1">Crédits insuffisants</p>
                  <p className="text-[#94a3b8] text-xs mb-3">
                    Vous avez {credits?.balance || 0} crédit(s). La génération coûte 10 crédits.
                  </p>
                  <button
                    onClick={() => navigate('/credits')}
                    className="flex items-center gap-2 bg-[#ffd93d] text-[#0f0e17] px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
                    data-testid="buy-credits-btn"
                  >
                    <Zap size={13} /> Recharger mes crédits <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleGenerate} className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    placeholder={t('dashboard.searchPlaceholder')}
                    className="w-full bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-xl pl-4 pr-4 py-3 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                    data-testid="dashboard-search-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={generating || !topic.trim() || user?.is_verified === false}
                  className="flex items-center gap-2 bg-[#ff6b6b] hover:opacity-90 text-white px-5 py-3 rounded-xl font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-opacity whitespace-nowrap"
                  data-testid="dashboard-generate-btn"
                >
                  {generating ? (
                    <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> En cours...</>
                  ) : (
                    <><Zap size={14} /> Générer</>
                  )}
                </button>
              </form>

              {error && (
                <div className="flex items-center gap-2 mt-2 text-[#ff6b6b] text-sm" data-testid="generation-error">
                  <AlertCircle size={14} /><span>{error}</span>
                </div>
              )}

              <p className="text-[11px] text-[#64748b] mt-2" data-testid="generation-cost-hint">
                {t('dashboard.generationCostHint')}
                {credits?.plan === 'explorateur' && <span className="text-[#20c997]"> · {t('dashboard.freePlanHint')}</span>}
              </p>

              <div className="flex flex-wrap gap-2 mt-3">
                {suggestions.map(s => (
                  <button
                    key={s}
                    onClick={() => setTopic(s)}
                    className="text-xs bg-[#16161e] border border-[#2a2935] text-[#94a3b8] hover:text-white hover:border-[#3a3945] px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`suggestion-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {referentials.length > 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowRefSection(v => !v)}
                    className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-[#20c997] transition-colors"
                    data-testid="toggle-referentials-btn"
                  >
                    <Target size={12} />
                    {selectedReferentials.length > 0
                      ? `${selectedReferentials.length} référentiel(s) ciblé(s)`
                      : 'Cibler un référentiel (optionnel)'}
                    <ChevronDown size={11} className={`transition-transform ${showRefSection ? 'rotate-180' : ''}`} />
                  </button>
                  {showRefSection && (
                    <div className="mt-2 p-3 bg-[#16161e] border border-[#2a2935] rounded-xl space-y-1.5" data-testid="referentials-panel">
                      <p className="text-xs text-[#64748b] mb-2">L'IA orientera le cours vers ces compétences :</p>
                      {referentials.slice(0, 8).map(ref => (
                        <label key={ref.name} className="flex items-center gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={selectedReferentials.includes(ref.name)}
                            onChange={e => {
                              if (e.target.checked) setSelectedReferentials(p => [...p, ref.name]);
                              else setSelectedReferentials(p => p.filter(r => r !== ref.name));
                            }}
                            className="w-3.5 h-3.5 accent-[#ff6b6b]"
                            data-testid={`ref-checkbox-${ref.name}`}
                          />
                          <span className="text-xs text-[#94a3b8] group-hover:text-white transition-colors">{ref.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Voix-off IA (toujours incluse) + Organisation ── */}
              <div className="mt-3 pt-3 border-t border-[#2a2935] flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-2 text-xs text-[#20c997]" data-testid="narration-included-badge">
                  <span className="w-2 h-2 rounded-full bg-[#20c997]" />
                  {t('dashboard.narrationIncluded')}
                </span>
                <select
                  value={organization}
                  onChange={e => setOrganization(e.target.value)}
                  className="bg-[#16161e] border border-[#2a2935] text-[#94a3b8] text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-[#20c997]/50"
                  data-testid="organization-selector"
                >
                  <option value="standard">Organisation : Standard</option>
                  <option value="sanctuaire">Sanctuaire Consulting</option>
                  <option value="numericli">Numericli</option>
                </select>
              </div>

              {/* ── Ressources externes (IA enrichie) ── */}
              <div className="mt-3 pt-3 border-t border-[#2a2935]">
                <button
                  type="button"
                  onClick={() => setShowResourceMode(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-[#20c997] transition-colors"
                  data-testid="toggle-resource-mode-btn"
                >
                  <Plus size={11} />
                  {showResourceMode ? 'Masquer les ressources' : 'Enrichir avec des ressources externes (IA avancée)'}
                  <ChevronDown size={10} className={`transition-transform ${showResourceMode ? 'rotate-180' : ''}`} />
                </button>

                {showResourceMode && (
                  <div className="mt-3 space-y-2" data-testid="resource-mode-panel">
                    <p className="text-xs text-[#64748b]">
                      Ajoutez jusqu'à 5 sources (sites web, YouTube, textes) — l'IA génèrera le cours depuis votre contenu.
                    </p>
                    {resources.map((res, i) => (
                      <div key={res.id} className="flex gap-2 items-start">
                        <select
                          value={res.type}
                          onChange={e => setResources(prev => prev.map(r => r.id === res.id ? { ...r, type: e.target.value, value: '' } : r))}
                          className="bg-[#16161e] border border-[#2a2935] text-[#94a3b8] text-xs rounded-lg px-2 py-2.5 shrink-0 focus:outline-none focus:border-[#20c997]/50"
                          data-testid={`resource-type-${i}`}
                        >
                          <option value="url">Site web</option>
                          <option value="youtube">YouTube</option>
                          <option value="text">Texte</option>
                        </select>
                        {res.type === 'text' ? (
                          <textarea
                            value={res.value}
                            onChange={e => setResources(prev => prev.map(r => r.id === res.id ? { ...r, value: e.target.value } : r))}
                            placeholder="Collez votre texte ici..."
                            rows={2}
                            className="flex-1 bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#20c997]/50 resize-none"
                            data-testid={`resource-value-${i}`}
                          />
                        ) : (
                          <input
                            value={res.value}
                            onChange={e => setResources(prev => prev.map(r => r.id === res.id ? { ...r, value: e.target.value } : r))}
                            placeholder={res.type === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://...'}
                            className="flex-1 bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-lg px-3 py-2.5 text-xs focus:outline-none focus:border-[#20c997]/50"
                            data-testid={`resource-value-${i}`}
                          />
                        )}
                        {resources.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setResources(prev => prev.filter(r => r.id !== res.id))}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#64748b] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors shrink-0 mt-0.5"
                            data-testid={`remove-resource-${i}`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    {resources.length < 5 && (
                      <button
                        type="button"
                        onClick={() => setResources(prev => [...prev, { id: Date.now(), type: 'url', value: '' }])}
                        className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-[#20c997] transition-colors"
                        data-testid="add-resource-btn"
                      >
                        <Plus size={11} /> Ajouter une ressource ({resources.length}/5)
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Lien création manuelle discret ── */}
              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => setCreationMode('manual')}
                  className="text-xs text-[#64748b] hover:text-[#94a3b8] transition-colors"
                  data-testid="switch-to-manual-link"
                >
                  Préférez-vous créer manuellement ? <span className="underline">Cliquez ici</span>
                </button>
              </div>
            </>
          ))}
        </div>

        {/* ── Quick actions ── */}
        <div data-testid="quick-actions">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/parcours')}
              data-testid="quick-action-parcours"
              className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex flex-col items-center gap-2 hover:border-[#3a3945] hover:bg-[#20202a] transition-all text-center"
            >
              <Route size={20} className="text-[#ff6b6b]" />
              <span className="text-xs font-medium text-[#94a3b8]">Mes Parcours</span>
            </button>
            <button
              onClick={() => navigate('/bilan')}
              data-testid="quick-action-bilan"
              className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex flex-col items-center gap-2 hover:border-[#3a3945] hover:bg-[#20202a] transition-all text-center"
            >
              <ClipboardList size={20} className={bilanStatus?.completed ? 'text-[#20c997]' : 'text-[#ffd93d]'} />
              <span className="text-xs font-medium text-[#94a3b8]">{bilanStatus?.completed ? 'Mon Bilan ✓' : 'Faire mon Bilan'}</span>
            </button>
            <button
              onClick={() => navigate('/credits')}
              data-testid="quick-action-credits"
              className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex flex-col items-center gap-2 hover:border-[#3a3945] hover:bg-[#20202a] transition-all text-center"
            >
              <Zap size={20} className="text-[#ffd93d]" />
              <span className="text-xs font-medium text-[#94a3b8]">
                {credits ? `${credits.balance} crédits` : 'Crédits'}
              </span>
            </button>
            <button
              onClick={() => navigate('/profile')}
              data-testid="quick-action-competences"
              className="bg-[#16161e] border border-[#2a2935] rounded-xl p-3.5 flex flex-col items-center gap-2 hover:border-[#3a3945] hover:bg-[#20202a] transition-all text-center"
            >
              <Award size={20} className="text-[#a78bfa]" />
              <span className="text-xs font-medium text-[#94a3b8]">Compétences</span>
            </button>
          </div>
        </div>

        {/* ── Mes formations récentes ── */}
        {activeCourses.length > 0 && (
          <div data-testid="recent-courses">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-base font-bold text-white">Mes formations</h2>
              {activeCourses.length > 4 && (
                <button
                  onClick={() => setShowAllCourses(v => !v)}
                  className="text-xs text-[#64748b] hover:text-white transition-colors flex items-center gap-1"
                  data-testid="toggle-all-courses-btn"
                >
                  {showAllCourses ? 'Réduire' : `Voir tout (${activeCourses.length})`}
                  <ChevronDown size={12} className={`transition-transform ${showAllCourses ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>

            <div className="space-y-2">
              {displayedCourses.map((course) => (
                <CourseRow
                  key={course.id}
                  course={course}
                  isCompleted={allProgress[course.id] === true}
                  courseSpace={course.space_id ? spaces.find(s => s.id === course.space_id) : null}
                  activeSpace={activeSpace}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onDelete={handleDelete}
                  onNavigate={handleNavigateCourse}
                />
              ))}
            </div>
          </div>
        )}

        {activeCourses.length === 0 && (
          <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-8 text-center" data-testid="empty-courses">
            <BookOpen size={32} className="text-[#2a2935] mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">Aucune formation pour l'instant.</p>
            <p className="text-[#64748b] text-xs mt-1">Générez votre premier cours ci-dessus !</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
